import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/laurierroseourika";

const MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=31.300988,-7.724062";

export default function Footer() {
  const { t } = useTranslation();

  const exploreLinks = [
    { path: "/gallery", label: t("nav.gallery") },
    { path: "/booking", label: t("nav.booking") },
    { path: "/restauration-full", label: t("nav.restauration") },
    { path: "/overview", label: t("nav.overview") },
    { path: "/transfer", label: t("nav.transfer") },
    { path: "/about", label: t("nav.about") },
  ];

  const year = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-ink text-cream">
      <div className="max-w-8xl mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-5"
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-2">
              Laurier <span className="text-brass-light">Rose</span>
            </h2>
            <div className="rule-gold my-6" />
            <p className="text-cream/60 leading-relaxed max-w-sm font-light">
              {t("footer.sections.about.content")}
            </p>
          </motion.div>

          {/* Explore */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="md:col-span-3"
          >
            <h3 className="eyebrow text-brass-light mb-6">{t("nav.home")}</h3>
            <ul className="space-y-3">
              {exploreLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-cream/70 hover:text-cream font-light tracking-wide transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="md:col-span-4"
          >
            <h3 className="eyebrow text-brass-light mb-6">
              {t("footer.sections.contact.title")}
            </h3>
            <ul className="space-y-4 text-cream/70 font-light">
              <li className="flex items-start gap-3">
                <span className="text-brass mt-0.5">◆</span>
                <span>77XG+WV7 Taljerft, Ourika 42452, Morocco</span>
              </li>
              <li>
                <a
                  href="tel:+212661919669"
                  className="hover:text-cream transition-colors duration-300"
                >
                  +212 661-919669
                </a>
              </li>
              <li>
                <a
                  href="mailto:laurierroseourika@gmail.com"
                  className="hover:text-cream transition-colors duration-300 break-all"
                >
                  laurierroseourika@gmail.com
                </a>
              </li>
              <li>
                <a
                  href={MAP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-brass-light hover:text-cream transition-colors duration-300"
                >
                  {t("footer.sections.location.viewOnMap")}
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-brass-light hover:text-cream transition-colors duration-300"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-cream/40 text-sm font-light tracking-wide">
            {t("footer.copyright").replace(/\b20\d{2}\b/, year)}
          </p>
          <p className="eyebrow text-cream/30 text-[10px]">
            Ourika Valley · Atlas Mountains · Morocco
          </p>
        </div>
      </div>
    </footer>
  );
}
